import { checkboxAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { mode, transparentize } from '@chakra-ui/theme-tools'

const baseStyleControl: SystemStyleFunction = (props) => {
  const { colorScheme: c } = props
  const boxShadowColor = transparentize(
    'checkbox-shadow-color',
    0.1,
  )(props.theme)
  const boxShadowColorHighlight = transparentize(
    'checkbox-shadow-highlight-color',
    0.1,
  )(props.theme)

  return {
    w: '100%',
    ml: '3px', // to align with the text
    border: '1px solid',
    borderRadius: 'sm',
    borderColor: 'checkbox-border',
    color: 'white',
    boxShadow: `0 0 0 3px ${boxShadowColor}`,

    _checked: {
      bg: mode(`${c}.500`, `${c}.200`)(props),
      borderColor: 'checkbox-border-checked',
      color: mode('white', 'gray.900')(props),
      boxShadow: `0 0 0 3px ${boxShadowColorHighlight}`,

      _hover: {
        bg: mode(`${c}.600`, `${c}.300`)(props),
        borderColor: mode(`${c}.200`, `${c}.300`)(props),
      },

      _disabled: {
        borderColor: mode('gray.200', 'transparent')(props),
        bg: mode(`${c}.200`, `${c}.300`)(props),
        color: mode('gray.500', 'whiteAlpha.500')(props),
      },
    },

    _indeterminate: {
      bg: mode(`${c}.500`, `${c}.200`)(props),
      borderColor: 'checkbox-border-checked',
      color: mode('white', 'gray.900')(props),
      ' > div > svg': {
        display: 'block',
      },
    },

    _disabled: {
      bg: mode('gray.100', 'whiteAlpha.100')(props),
      borderColor: mode('gray.100', 'transparent')(props),
    },

    _hover: {
      boxShadow: `0 0 0 3px ${boxShadowColorHighlight}`,
    },

    _focus: {
      boxShadow: `0 0 0 3px ${boxShadowColorHighlight}`,
    },

    _invalid: {
      borderColor: mode('red.500', 'red.300')(props),
    },
  }
}

const baseStyleContainer: SystemStyleObject = {
  display: 'grid',
  gap: 5,
  gridTemplateColumns: 'auto auto',
  alignContent: 'center',
  alignItems: 'center',
  justifyContent: 'start',
  _disabled: { cursor: 'not-allowed' },
}

const baseStyleLabel: SystemStyleObject = {
  color: 'checkbox-color',
  fontWeight: 'medium',
  lineHeight: 'base',
  userSelect: 'none',
  ml: 0,
  _disabled: { opacity: 0.4 },
  _checked: { fontWeight: 'bold' },
}

const baseStyleIcon: SystemStyleObject = {
  display: 'none',
  transitionProperty: 'transform',
  transitionDuration: 'normal',
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  icon: baseStyleIcon,
  container: baseStyleContainer,
  control: baseStyleControl(props),
  label: baseStyleLabel,
})

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  sm: {
    control: { h: 3, w: 3 },
    label: { fontSize: 'sm' },
    icon: { fontSize: '0.45rem' },
  },
  md: {
    control: { w: 4, h: 4 },
    label: { fontSize: 'md' },
    icon: { fontSize: '0.625rem' },
  },
  lg: {
    control: { w: 5, h: 5 },
    label: { fontSize: 'lg' },
    icon: { fontSize: '0.625rem' },
  },
}

const defaultProps = {
  size: 'md',
  colorScheme: 'green',
}

export const Checkbox = {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps,
}
